'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CharacterPricingDialog } from "@/components/translate/character-pricing-dialog";
import { Zap } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useState } from 'react';

interface UsageBalanceResult {
  availableCharacters: number;
  isAnonymous: boolean;
  creditUnit?: string;
}

interface UsageIndicatorProps {
  className?: string;
  showDetails?: boolean;
  onGetMoreCharactersClick?: () => void;
}

export function AccountCreditsPanel({ className, showDetails = false, onGetMoreCharactersClick }: UsageIndicatorProps) {
  const queryClient = useQueryClient();
  const t = useTranslations('usage');
  const [showPricingDialog, setShowPricingDialog] = useState(false);

  const { data: usage, isLoading: loading } = useQuery({
    queryKey: ['usage'],
    queryFn: async () => {
      const response = await fetch('/api/usage');

      if (response.status === 401) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch usage');
      }

      return response.json() as Promise<UsageBalanceResult>;
    },
    retry: false,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // Refresh usage after translation - listen for custom events
  useEffect(() => {
    const handleTranslationComplete = () => {
      queryClient.invalidateQueries({ queryKey: ['usage'] });
    };

    window.addEventListener('translation-complete', handleTranslationComplete);
    return () => window.removeEventListener('translation-complete', handleTranslationComplete);
  }, [queryClient]);

  if (loading) {
    return (
      <div className={className}>
        {/* Shimmer loading effect */}
        <div className="animate-pulse">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div className="h-3 w-3 bg-border rounded"></div>
              <div className="h-3 w-20 bg-border rounded"></div>
            </div>
            <div className="h-4 w-8 bg-border rounded"></div>
          </div>

          {showDetails && (
            <div className="mt-2">
              <div className="w-full h-1 bg-border rounded-full"></div>
              <div className="h-3 w-24 bg-border rounded mt-1"></div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (!usage) {
    return null;
  }

  const isLowBalance = usage.availableCharacters < 1000;
  const isCriticallyLowBalance = usage.availableCharacters < 250;

  return (
    <div className={className}>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1">
          <Zap className={`h-3 w-3 ${isCriticallyLowBalance ? 'text-red-500' : isLowBalance ? 'text-orange-500' : 'text-green-500'}`} />
          <span className="text-xs text-muted-foreground">
            {usage.availableCharacters.toLocaleString()} {t('availableCredits')}
          </span>
        </div>

        {usage.isAnonymous && (
          <Badge variant="secondary" className="text-xs">
            {t('free')}
          </Badge>
        )}
      </div>

      {showDetails && (
        <div className="mt-2">
          <div className="text-xs text-muted-foreground mt-1">
            {usage.availableCharacters.toLocaleString()} {t('credits')}
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-start">
        <Button
          type="button"
          variant="link"
          className="h-auto p-0 text-xs"
          onClick={() => {
            if (onGetMoreCharactersClick) {
              onGetMoreCharactersClick();
              return;
            }

            setShowPricingDialog(true);
          }}
        >
          {t('getMoreCharacters')}
        </Button>
      </div>

      {/* Handle showing pricing dialog if no custom handler is provided, and yes this is AI rubbish */}
      {!onGetMoreCharactersClick && (
        <CharacterPricingDialog open={showPricingDialog} onOpenChange={setShowPricingDialog} />
      )}

    </div>
  );
}

export const UsageIndicator = AccountCreditsPanel;
